import { yupResolver } from "@hookform/resolvers/yup"
import Alert from "@mui/material/Alert"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import * as yup from "yup"

import CheckBox from "@components/checkbox"
import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import {
  CAMPAIGN_LINKS_QUERY_KEYS,
  setCampaignLinksDisabled,
  setCustomerLinksDisabled
} from "@domain/campaign_links"
import useControlledForm from "@hooks/use_controlled_form"
import useTranslate from "@hooks/use_translate"
import {
  type CampaignLinksToggleFormData,
  type CampaignLinksToggleTarget
} from "@models/campaign_links"
import { showSuccessToast } from "@utils/toast"

const DEFAULTS: CampaignLinksToggleFormData = { reason: "", pause: true }

export default function CampaignLinksToggle({
  isOpen,
  setClose,
  target,
  disable
}: {
  isOpen: boolean
  setClose: () => void
  target: CampaignLinksToggleTarget
  /** True turns the links off; false turns them back on. */
  disable: boolean
}) {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  const schema = yup.object({
    reason: disable
      ? yup.string().trim().min(3, translate("campaign_links_reason_required"))
      : yup.string(),
    pause: yup.boolean()
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<CampaignLinksToggleFormData>({
    resolver: yupResolver(schema) as never,
    mode: "all",
    defaultValues: DEFAULTS
  })

  useEffect(() => {
    if (isOpen) reset(DEFAULTS)
  }, [isOpen, reset])

  const onSubmit = async (data: CampaignLinksToggleFormData) => {
    setLoading(true)
    const input = {
      disabled: disable,
      ...(disable && { reason: data.reason.trim(), pause: data.pause })
    }
    let pausedCount = 0
    if (target.kind === "campaign") {
      const response = await setCampaignLinksDisabled({
        campaignId: target.campaignId,
        ...input
      })
      setLoading(false)
      if (!response.isSuccess) return
      pausedCount = response.data?.paused ? 1 : 0
    } else {
      const response = await setCustomerLinksDisabled({
        customerId: target.customerId,
        ...input
      })
      setLoading(false)
      if (!response.isSuccess) return
      pausedCount = response.data?.pausedCampaigns ?? 0
    }

    showSuccessToast(
      disable
        ? translate("campaign_links_turned_off", { count: pausedCount })
        : translate("campaign_links_turned_on")
    )
    await Promise.all(
      Object.values(CAMPAIGN_LINKS_QUERY_KEYS).map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    )
    setClose()
  }

  return (
    <DialogProvider
      title={translate(
        disable
          ? "campaign_links_turn_off_title"
          : "campaign_links_turn_on_title"
      )}
      isOpen={isOpen}
      setClose={setClose}
      size="medium"
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: setClose,
        disabled: loading
      }}
      rightButton={{
        text: translate(
          disable ? "campaign_links_turn_off" : "campaign_links_turn_on"
        ),
        color: disable ? "error" : "primary",
        onClick: handleSubmit(onSubmit),
        loading,
        disabled: !isValid || loading
      }}
    >
      <Stack gap={2}>
        <Typography variant="subtitle1">{target.label}</Typography>
        {disable ? (
          <>
            <Alert severity="warning">
              {translate(
                target.kind === "campaign"
                  ? "campaign_links_turn_off_campaign_hint"
                  : "campaign_links_turn_off_customer_hint"
              )}
            </Alert>
            <TextInput<CampaignLinksToggleFormData>
              name="reason"
              control={control}
              disabled={loading}
              multiline
              rows={3}
              label={translate("campaign_links_reason")}
              placeholder={translate("campaign_links_reason_placeholder")}
              required
            />
            <CheckBox<CampaignLinksToggleFormData>
              name="pause"
              control={control}
              disabled={loading}
              defaultValue={DEFAULTS.pause}
              label={translate("campaign_links_pause_sending")}
            />
          </>
        ) : (
          <Alert severity="info">
            {translate("campaign_links_turn_on_hint")}
          </Alert>
        )}
      </Stack>
    </DialogProvider>
  )
}
